import { formatPtoGrantedDate } from '@/helpers/formatPtoGrantedDate';
import {
  isPackageEditable,
  PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE,
  shouldShowPackageUnderReviewAlert,
} from '@/helpers/packageEditability';
import { validateDate } from '@/helpers/validateDate';
import usePermissions from '@/hooks/usePermissions';
import { useToastSuccessSavedPackageMessage } from '@/hooks/useSavedPackageMessage';
import {
  useSavePostActivationPackageMutation,
  useSubmitPostActivationPackageMutation,
} from '@/services/postActivationPackage';
import { Alert, toast } from '@palmetto/palmetto-components';
import { Formik, FormikValues } from 'formik';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import {
  DeepPartial,
  PostActivationPackage as IPostActivationPackage,
  MilestoneType,
  PostActivationPackageActions,
  ProgramType,
  QueueDocumentResponse,
  QueueStatus,
  QueueType,
  UserPermissions,
} from 'types';

import { DATE_FORMAT } from '../../helpers/dates';
import { useGetAccountQuery } from '../../services/accounts';
import { isErrorWithData } from '../../services/helpers';
import { useGetQueuesByAccountIdQuery } from '../../services/queues';
import { ActionsList } from '../ActionsList';
import { ProofOfPto } from '../ActivationMilestonePackage/ProofOfPto';
import { useAuth } from '../auth/authProvider';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import { EnhancedFormikForm } from '../EnhancedFormikForm';
import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';
import { Footer } from '../MilestonePackagePartials/Footer';
import { QueueBanner } from '../MilestonePackagePartials/QueueBanner';
import { NotesDrawer } from '../NotesDrawer/NotesDrawer';
import { QueueFlagsProvider } from '../QueueFlags/QueueFlagsContext';
import { SkeletonBox } from '../SkeletonBox';

export const formatPostActivationPackageData = (values: FormikValues): DeepPartial<IPostActivationPackage> => {
  return {
    ptoGrantedDate: formatPtoGrantedDate(values),
  };
};
const postActivationPackageSchema = yup.object().shape({
  ptoGrantedDate: yup.string().required('PTO Granted Date is required'),
});

export const PostActivationPackage = ({
  canSubmitPostActivationPackage,
}: {
  canSubmitPostActivationPackage: boolean;
}) => {
  const navigate = useNavigate();
  const { id = '', action = PostActivationPackageActions.review } = useParams<{
    id: string;
    action: PostActivationPackageActions;
  }>();
  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(id);

  const { data: queuesData, isLoading: isQueueLoading } = useGetQueuesByAccountIdQuery({
    accountId: id,
    types: [QueueType.postActivationPackage, QueueType.permissionToOperate],
  });
  const queue = queuesData?.find((q: QueueDocumentResponse) => q.type === QueueType.postActivationPackage);
  const ptoQueue = queuesData?.find((q: QueueDocumentResponse) => q.type === QueueType.permissionToOperate);

  const [triggerSave] = useSavePostActivationPackageMutation();
  const [triggerSubmit] = useSubmitPostActivationPackageMutation();
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const userPermissions = usePermissions();
  const isLightReachQCTech = userPermissions.includes(UserPermissions.lightReachQCTech);

  const canSubmitReview = action === PostActivationPackageActions.review && queue;

  const lockPtoField = isFinCoUser
    ? false
    : [QueueStatus.approved, QueueStatus.submitted, QueueStatus.resubmitted].includes(ptoQueue?.status);

  const isLoading = isQueueLoading || isAccountLoading;
  const allowsAction = useIsOrgReadOnly();
  const canEditPackage = isPackageEditable({ queue, isFinCoUser });
  const showUnderReviewAlert = shouldShowPackageUnderReviewAlert({
    queue,
    isFinCoUser,
  });

  const initialValues = {
    ptoGrantedDate: account?.ptoGrantedDate
      ? DateTime.fromISO(account?.ptoGrantedDate).toFormat(DATE_FORMAT)
      : undefined,
  };
  const triggerSuccessMessage = useToastSuccessSavedPackageMessage(id, MilestoneType.postActivation);

  useEffect(() => {
    if (!action || !PostActivationPackageActions[action]) {
      navigate(`/accounts/${id}`, { replace: true });
    }
    if (action === PostActivationPackageActions.review && (!isFinCoUser || !isLightReachQCTech)) {
      if (queue) {
        navigate(`/accounts/${id}/post-activation-package/edit`, { replace: true });
      } else {
        navigate(`/accounts/${id}/post-activation-package/create`, { replace: true });
      }
    }
  }, [id, navigate, action, isFinCoUser, isLightReachQCTech, queue]);

  const handleSave = async ({ accountId, data }: { accountId: string; data: FormikValues }) => {
    const formattedData = formatPostActivationPackageData(data);
    try {
      await triggerSave({ accountId, savePackage: formattedData }).unwrap();
      triggerSuccessMessage();
    } catch (e) {
      console.error(e);
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        throw new Error(errorMessage);
      } else {
        throw new Error('Error saving Post Activation Package');
      }
    }
  };
  const handleSubmit = async (values: FormikValues, { setSubmitting }: any) => {
    try {
      const formattedData = formatPostActivationPackageData(values) as IPostActivationPackage;
      await triggerSubmit({ accountId: id, postActivationPackage: formattedData }).unwrap();
      toast.success('Post Activation Package Submitted');
      navigate(`/accounts/${id}`);
    } catch (e: any) {
      console.error(e);
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        toast.error(errorMessage);
      } else {
        toast.error('Error saving Post Activation Package');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleValidation = (values: any) => {
    const errors = {} as any;

    if (account.programType === ProgramType.doePr) {
      if (!values.ptoGrantedDate) {
        errors.ptoGrantedDate = 'PTO Granted Date is required';
      } else if (!validateDate(values.ptoGrantedDate)) {
        errors.ptoGrantedDate = 'PTO Granted Date is invalid. Must be in the format MM-DD-YYYY';
      }
    }
    return errors;
  };

  if (isLoading) {
    return (
      <MainContainer>
        <FormSection title="Loading...">
          <SkeletonBox height="25px" width="100" />
        </FormSection>
      </MainContainer>
    );
  }

  return (
    <QueueFlagsProvider queueId={queue?.id}>
      <NotesDrawer isOpen={isNotesOpen} setIsOpen={setIsNotesOpen} queueType={QueueType.postActivationPackage} />
      <Helmet>
        <title>{`${account?.primaryApplicantName} Post Activation Package`}</title>
      </Helmet>
      <MainContainer>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize={true}
          validateOnChange={false}
          validate={handleValidation}
          validationSchema={postActivationPackageSchema}
        >
          <EnhancedFormikForm>
            <ActionsList
              milestoneType={MilestoneType.postActivation}
              accountId={queue?.account?.id}
              canSubmitPackage={canSubmitPostActivationPackage && allowsAction}
              isLoading={isLoading}
              isFirstSubmission={!queue}
              canSubmitReview={canSubmitReview && allowsAction}
              onSave={handleSave}
              disabled={!allowsAction}
              initialValues={initialValues}
              onSubmit={handleSubmit}
              queue={queue}
            />
            {queue && (queue.status === QueueStatus.rejected || isFinCoUser) ? (
              <QueueBanner
                setIsNotesOpen={setIsNotesOpen}
                queueType={QueueType.postActivationPackage}
                isFinCoUser={isFinCoUser}
              />
            ) : null}
            {action === PostActivationPackageActions.review && !canSubmitReview ? (
              <Alert
                variant="warning"
                title="Post Activation Package Not Submitted"
                message="This post activation package is not ready to be reviewed yet. It has not been submitted."
                hasIcon
              />
            ) : null}
            {showUnderReviewAlert ? (
              <Alert
                variant="warning"
                title="Post Activation Package In Review Queue"
                message={PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE}
                hasIcon
              />
            ) : null}
            <ProofOfPto
              disabled={lockPtoField}
              isReviewMode={action === PostActivationPackageActions.review}
              isPackageEditable={canEditPackage}
            />
            <Footer
              canSubmitPackage={canSubmitPostActivationPackage && allowsAction}
              isLoading={isLoading}
              isFirstSubmission={!queue}
              canSubmitReview={canSubmitReview && allowsAction}
              onSave={handleSave}
              disabled={!allowsAction || !canEditPackage}
            />
          </EnhancedFormikForm>
        </Formik>
      </MainContainer>
    </QueueFlagsProvider>
  );
};
